/**
 * Dividend and split events from a market-data vendor, normalised. Vendors here: Twelve Data (/dividends, /splits)
 * and EODHD (/div, /splits). Mapping is pure (`mapXxx`); network goes through the injected `ProviderContext.fetch`.
 * Neither vendor publishes Australian franking, so `frankingPct` is null and the user fills it from the registry
 * statement.
 */
import { z } from 'zod';
import { dec, minorUnits } from '../money.js';
import { assertDateRange, currencyForSymbol, getJson, IsoDateString, normaliseCurrency, NumericString, parseOrThrow, requireKey, } from './provider.js';
import { twelveDataGet, twelveDataSymbolQuery } from './twelvedata.js';
export { createTwelveDataProvider } from './twelvedata.js';
const perUnit = (amount, currency) => dec(amount).mul(10 ** minorUnits(currency)).toDecimalPlaces(6).toString();
const Num = z.union([NumericString, z.number().finite()]);
const DateOrNull = z.union([IsoDateString, z.literal(''), z.literal('0000-00-00')]).nullish().transform((d) => (d ? (d === '0000-00-00' ? null : d) : null));
const positive = (n) => Number.isFinite(n) && n > 0;
// ───────────── Twelve Data ─────────────
const TdDividends = z.object({
    meta: z.object({ currency: z.string().nullish() }).prefault({}),
    dividends: z.array(z.object({ ex_date: IsoDateString, amount: Num })).prefault([]),
});
/** Twelve Data's from_factor/to_factor: AAPL's 2020 "4-for-1 split" is from_factor 4, to_factor 1 (new per old). */
const TdSplits = z.object({ splits: z.array(z.object({ date: IsoDateString, from_factor: Num, to_factor: Num })).prefault([]) });
/** Twelve Data /dividends body → events (pay and record dates are not published). */
export const mapTwelveDataDividends = (body, fallbackCurrency = 'USD') => {
    const b = parseOrThrow(TdDividends, body, 'twelvedata');
    const currency = normaliseCurrency(b.meta.currency ?? fallbackCurrency);
    return b.dividends.filter((d) => Number(d.amount) > 0).map((d) => ({
        exDate: d.ex_date, recordDate: null, payDate: null, perUnitCents: perUnit(d.amount, currency), currency, frankingPct: null,
    }));
};
export const mapTwelveDataSplits = (body) => parseOrThrow(TdSplits, body, 'twelvedata').splits
    .map((s) => ({ exDate: s.date, ratioFrom: Number(s.to_factor), ratioTo: Number(s.from_factor) }))
    .filter((s) => positive(s.ratioFrom) && positive(s.ratioTo) && s.ratioFrom !== s.ratioTo);
export const createTwelveDataEventsProvider = (ctx) => ({
    id: 'twelvedata',
    async dividends(symbol, from, to) {
        assertDateRange('twelvedata', from, to);
        const ex = /^([A-Z]+):/i.exec(symbol)?.[1]?.toUpperCase();
        return mapTwelveDataDividends(await twelveDataGet(ctx, '/dividends', `${twelveDataSymbolQuery(symbol)}&start_date=${from}&end_date=${to}`), ex === 'ASX' ? 'AUD' : 'USD');
    },
    async splits(symbol, from, to) {
        assertDateRange('twelvedata', from, to);
        return mapTwelveDataSplits(await twelveDataGet(ctx, '/splits', `${twelveDataSymbolQuery(symbol)}&start_date=${from}&end_date=${to}`));
    },
});
// ───────────── EODHD ─────────────
const EodDividends = z.array(z.object({
    date: IsoDateString, recordDate: DateOrNull, paymentDate: DateOrNull,
    value: Num.nullish(), unadjustedValue: Num.nullish(), currency: z.string().nullish(),
}));
const EodSplits = z.array(z.object({ date: IsoDateString, split: z.string().regex(/^\d+(\.\d+)?\/\d+(\.\d+)?$/) }));
/** EODHD /div body → events. `unadjustedValue` (as paid) wins over the split-adjusted `value`. */
export const mapEodhdDividends = (body, fallbackCurrency = 'USD') => parseOrThrow(EodDividends, body, 'eodhd').flatMap((d) => {
    const amount = d.unadjustedValue ?? d.value;
    if (amount === null || amount === undefined || Number(amount) <= 0)
        return [];
    const currency = normaliseCurrency(d.currency ?? fallbackCurrency);
    return [{ exDate: d.date, recordDate: d.recordDate, payDate: d.paymentDate, perUnitCents: perUnit(amount, currency), currency, frankingPct: null }];
});
/** EODHD /splits body (`"2.000000/1.000000"` = new/old) → events. */
export const mapEodhdSplits = (body) => parseOrThrow(EodSplits, body, 'eodhd')
    .map((s) => { const [to, from] = s.split.split('/').map(Number); return { exDate: s.date, ratioFrom: from ?? 0, ratioTo: to ?? 0 }; })
    .filter((s) => positive(s.ratioFrom) && positive(s.ratioTo) && s.ratioFrom !== s.ratioTo);
export const createEodhdEventsProvider = (ctx) => {
    const url = (path, symbol, from, to) => `https://eodhd.com/api${path}/${encodeURIComponent(symbol)}?from=${from}&to=${to}&fmt=json&api_token=${encodeURIComponent(requireKey(ctx, 'eodhd'))}`;
    return {
        id: 'eodhd',
        async dividends(symbol, from, to) {
            assertDateRange('eodhd', from, to);
            return mapEodhdDividends(await getJson(ctx, 'eodhd', url('/div', symbol, from, to)), currencyForSymbol('eodhd', symbol) ?? 'USD');
        },
        async splits(symbol, from, to) {
            assertDateRange('eodhd', from, to);
            return mapEodhdSplits(await getJson(ctx, 'eodhd', url('/splits', symbol, from, to)));
        },
    };
};
//# sourceMappingURL=marketEvents.js.map